import { useState } from "react";
import { Modal } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

import { formatNotificationMessage } from "./notificationFormatter";
import { notificationTypeConfig } from "./notificationConfig";
import { formatNotificationDate, groupNotificationsByType } from "./notificationUtils";
import { markNotificationsRead } from "../pages/api/notification";

const NotificationModal = ({ show, onHide, notifications, setNotifications }) => {
  const userId = localStorage.getItem("id");
  const token = localStorage.getItem("token");

  const unreadNotifications = notifications.filter((n) => !n.read);
  const unreadCount = unreadNotifications.length;
  const groupedByType = groupNotificationsByType(unreadNotifications);
  const [expanded, setExpanded] = useState({});
  const toggleGroup = (type) => setExpanded((prev) => ({ ...prev, [type]: !prev[type] }));

  const getConfig = (type) =>
    notificationTypeConfig[type] ||
    notificationTypeConfig[String(type || "").replace(/_/g, " ")] ||
    notificationTypeConfig.DEFAULT;

  // green tick: mark one as read (removes it)
  const markAsRead = async (id) => {
    try {
      await markNotificationsRead(userId, [id], token);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Failed to mark as read", err);
    }
  };

  // mark all as read (removes all)
  const clearAll = async () => {
    const ids = unreadNotifications.map((n) => n.id);
    if (ids.length === 0) return;
    try {
      await markNotificationsRead(userId, ids, token);
      setNotifications([]);
    } catch (err) {
      console.error("Failed to clear notifications", err);
    }
  };

  const renderItem = (n, extraClass = "", toggle = null, showCheck = true) => {
    const config = getConfig(n.type);
    return (
      <div key={n.id} className={`nm-item ${extraClass}`}>
        <div className="nm-ico" style={{ background: `${config.color}1f`, color: config.color }}>
          {config.icon}
        </div>
        <div className="nm-body">
          <div className="nm-msg">{formatNotificationMessage(n)}</div>
          <div className="nm-meta">
            <span className="nm-type" style={{ color: config.color }}>
              {String(n.type || "").replace(/_/g, " ").toLowerCase()}
            </span>
            <span>•</span>
            <span>{formatNotificationDate(n.createdAt)}</span>
          </div>
        </div>
        <div className="nm-actions">
          {toggle}
          {showCheck && (
            <button className="nm-check" title="Mark as read" onClick={() => markAsRead(n.id)}>
              <i className="bi bi-check2"></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="nm-modal"
      contentClassName="nm-content"
      backdropClassName="nm-backdrop"
    >
      <style>{`
        .modal-backdrop.nm-backdrop { --bs-backdrop-opacity: 0.12; }
        .modal-backdrop.nm-backdrop.show { opacity: 0.12; }
        .nm-modal { position: fixed !important; top: 64px; right: 16px; margin: 0 !important;
                    width: 400px; max-width: calc(100vw - 32px) !important; }
        .modal.fade .nm-modal { transform: translateY(-8px); transition: transform .2s ease-out; }
        .modal.show .nm-modal { transform: none; }
        .nm-content { border: 1px solid #e8e8f4; border-radius: 16px; overflow: hidden;
                      box-shadow: 0 18px 50px rgba(30,27,75,0.18); }

        .nm-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px 10px; }
        .nm-title-wrap { display: flex; align-items: center; gap: 8px; }
        .nm-title { margin: 0; font-size: 17px; font-weight: 700; color: #1e1b4b; }
        .nm-count { background: #624bff; color: #fff; font-size: 11px; font-weight: 600; border-radius: 999px; padding: 1px 8px; }
        .nm-close { background: none; border: none; color: #9ca3af; font-size: 15px; width: 28px; height: 28px;
                    border-radius: 8px; cursor: pointer; }
        .nm-close:hover { background: #f3f4f6; color: #374151; }

        .nm-list { max-height: 380px; overflow-y: auto; padding: 0 10px 6px; }
        .nm-item { display: flex; align-items: flex-start; gap: 12px; padding: 11px 10px; margin-bottom: 4px;
                   border-radius: 12px; background: #f4f3ff; transition: background .15s; }
        .nm-item:hover { background: #ebe8ff; }
        .nm-ico { width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0; display: flex;
                  align-items: center; justify-content: center; font-size: 17px; }
        .nm-body { flex: 1; min-width: 0; }
        .nm-msg { font-size: 13.5px; line-height: 1.4; color: #1f2937; word-break: break-word; }
        .nm-meta { display: flex; align-items: center; gap: 6px; margin-top: 3px; font-size: 11px; color: #9ca3af; }
        .nm-meta .nm-type { font-weight: 600; text-transform: capitalize; }
         .nm-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0; }
        .nm-check { flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; border: none; background: #fff;
                    color: #22c55e; font-size: 13px; cursor: pointer; display: flex; align-items: center;
                    justify-content: center; box-shadow: 0 1px 3px rgba(0,0,0,0.08); transition: all .15s; }
        .nm-check:hover { background: #22c55e; color: #fff; }

        .nm-group { margin-bottom: 8px; }
        .nm-more { display: inline-flex; align-items: center; gap: 3px; border: none; background: #fff;
                   font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; cursor: pointer;
                   box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
        .nm-more:hover { background: #ebe8ff; }
        .nm-item.sub { margin-left: 18px; background: #fafaff; border-left: 2px solid #e0dcff;
                       border-radius: 8px 12px 12px 8px; }

        .nm-empty { text-align: center; padding: 40px 0 36px; color: #6b7280; font-size: 13px; }
        .nm-empty-ico { width: 56px; height: 56px; border-radius: 50%; background: #f4f3ff; color: #624bff;
                        font-size: 24px; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px; }

        .nm-footer { padding: 10px 18px 16px; }
        .nm-markall { width: 100%; border: none; border-radius: 10px; padding: 10px; font-size: 13.5px; font-weight: 600;
                      color: #fff; background: #624bff; cursor: pointer; display: flex; align-items: center;
                      justify-content: center; gap: 8px; transition: background .15s; }
        .nm-markall:hover { background: #4f3ae0; }
      `}</style>

      <div className="nm-header">
        <div className="nm-title-wrap">
          <h5 className="nm-title">Notifications</h5>
          {unreadCount > 0 && <span className="nm-count">{unreadCount > 99 ? "99+" : unreadCount}</span>}
        </div>
        <button className="nm-close" onClick={onHide} title="Close">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div className="nm-list">
        {unreadCount === 0 ? (
          <div className="nm-empty">
            <div className="nm-empty-ico"><i className="bi bi-bell"></i></div>
            You're all caught up
          </div>
        ) : (
          Object.entries(groupedByType).map(([type, items]) => {
            const [latest, ...rest] = items;
            const isOpen = !!expanded[type];
            return (
              <div key={type} className="nm-group">
                {renderItem(
                  latest,
                  "",
                  rest.length > 0 ? (
                    <button
                      className="nm-more"
                      style={{ color: getConfig(type).color }}
                      onClick={() => toggleGroup(type)}
                      title={`${rest.length} more ${type.replace(/_/g, " ").toLowerCase()}`}
                    >
                      {isOpen ? "Hide" : `+${rest.length}`}
                      <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
                    </button>
                  ) : null,
                  rest.length === 0 || isOpen
                )}
                {isOpen && rest.map((n) => renderItem(n, "sub"))}
              </div>
            );
          })
        )}
      </div>

      {unreadCount > 0 && (
        <div className="nm-footer">
          <button className="nm-markall" onClick={clearAll} title="Mark all as read">
            <i className="bi bi-check2-all"></i> Mark all as read
          </button>
        </div>
      )}
    </Modal>
  );
};

export default NotificationModal;
